import { isNumeric } from '../../util/helpers';


/**
 * Terminal procedure document type
 */
export enum ProcedureType {
  /**
   * Airport surface hot spot document
   */
  HotSpot,

  /**
   * Airport diagram
   */
  AirportDiagram,

  /**
   * Standard terminal arrival route (STAR)
   */
  STAR,

  /**
   * Alternate or takeoff minimums
   */
  TakeoffMinimums,

  /**
   * Instrument approach procedure (IAP)
   */
  IAP,

  /**
   * Land and hold short operations (LAHSO)
   */
  LAHSO,

  /**
   * Departure procedure (DP)
   */
  DP,

  /**
   * "Attention All Users" page (AAUP)
   */
  AAUP,

  /**
   * Obstacle departure procedure (ODP)
   */
  ODP,

  /**
   * Chart supplement
   */
  CHS
}


/**
 * For new or recently updated terminal procedures, indicates the type of change that occurred in the last data cycle.
 */
export enum ChangeType {
  Added,
  Changed,
  Deleted
}


/**
 * Terminal procedure data cycle model.
 */
export enum TerminalProcedureCycle {
  /**
   * Represents terminal procedures that are effective in the current data cycle.
   */
  Current = 'current',

  /**
   * Represents terminal procedures that will become effective in the next data cycle.
   */
  Next = 'next'
}


/**
 * Represents the available formats in which a batch of terminal procedures can be downloaded.
 */
export enum TerminalProcedureBatchFormat {
  PDF = 'pdf',
  ZIP = 'zip'
}


/**
 * Terminal procedure data for an airport
 */
export class TerminalProcedure {
  /**
   * Unique identifier for this record in the database
   */
  id: number;

  /**
   * Airport record ID for which this record applies
   */
  airportId: number;

  /**
   * Volume name in the print version of the terminal procedures publication where this instrument procedure appears.
   */
  volume: string;

  /**
   * PDF file name
   */
  filename: string;

  /**
   * Identifies the type of terminal procedure
   */
  type: ProcedureType;

  /**
   * Size of the PDF (in bytes)
   */
  filesize: number;

  /**
   * Effective from date
   */
  effDateFrom: Date;

  /**
   * Effective to date
   */
  effDateTo: Date;

  /**
   * PDF URL to use for downloading this procedure
   */
  url: string;

  /**
   * True if this procedure is for military use (TODO: Figure out if it's also OK for civil use, or if we should hide these).
   */
  military: boolean;

  /**
   * For new or recently updated terminal procedures, indicates the type of change that occurred in the last data cycle.
   */
  changeType: ChangeType;

  /**
   * Procedure name
   */
  name: string;

  /**
   * Returns the full filename of the procedure (including the volume and PDF name). This is the filename that
   * should be used when using the batch utility to download multiple procedures as a single PDF or ZIP file.
   * @returns {string}
   */
  get fullFilename(): string {
    return `${this.volume}/${this.filename}`;
  }

  /**
   * Parses terminal procedure
   * @param data {any} Terminal procedure data as returned by API
   * @return {TerminalProcedure} Parsed terminal procedure
   */
  static parse(data: any): TerminalProcedure {
    return Object.assign(new TerminalProcedure(), {
      id: data.terminalProcedureId,
      airportId: data.airportId,
      volume: data.volume,
      filename: data.pageName,
      type: this.parseProcedureType(data.pageType),
      filesize: isNumeric(data.pdfSize) ? +data.pdfSize : null,
      effDateFrom: data.effDateFrom ? new Date(data.effDateFrom) : null,
      effDateTo: data.effDateTo ? new Date(data.effDateTo) : null,
      url: data.url,
      military: data.military === 'M',
      changeType: this.parseChangeType(data.userAction),
      name: data.chartName
    });
  }

  /**
   * Parses procedure type
   * @param value {any} Procedure type as returned by API
   * @return {ProcedureType} Parsed procedure type
   */
  static parseProcedureType(value: any): ProcedureType {
    switch (value) {
      case 'HOT':
        return ProcedureType.HotSpot;
      case 'APD':
        return ProcedureType.AirportDiagram;
      case 'STAR':
        return ProcedureType.STAR;
      case 'MIN':
        return ProcedureType.TakeoffMinimums;
      case 'IAP':
        return ProcedureType.IAP;
      case 'LAH':
        return ProcedureType.LAHSO;
      case 'DP':
        return ProcedureType.DP;
      case 'DAU':
        return ProcedureType.AAUP;
      case 'ODP':
        return ProcedureType.ODP;
      case 'CHS':
        return ProcedureType.CHS;
      default:
        return null;
    }
  }

  /**
   * Formats procedure type (abbreviation) for dislay
   * @param value {ProcedureType} Procedure type
   * @return {string} Formatted procedure type
   */
  static formatProcedureType(value: ProcedureType): string {
    switch (value) {
      case ProcedureType.HotSpot:
        return 'HOT';
      case ProcedureType.AirportDiagram:
        return 'APD';
      case ProcedureType.STAR:
        return 'STAR';
      case ProcedureType.TakeoffMinimums:
        return 'MIN';
      case ProcedureType.IAP:
        return 'IAP';
      case ProcedureType.LAHSO:
        return 'LAHSO';
      case ProcedureType.DP:
        return 'DP';
      case ProcedureType.AAUP:
        return 'AAUP';
      case ProcedureType.ODP:
        return 'ODP';
      case ProcedureType.CHS:
        return 'CHS';
      default:
        return null;
    }
  }

  /**
   * Formats procedure type (full term) for dislay
   * @param value {ProcedureType} Procedure type
   * @param [plural] {boolean} Plural (defaults to false)
   * @return {string} Formatted procedure type
   */
  static formatProcedureTypeFull(value: ProcedureType, plural = false): string {
    switch (value) {
      case ProcedureType.HotSpot:
        return 'Hot Spot Diagram' + (plural ? 's' : '');
      case ProcedureType.AirportDiagram:
        return 'Airport Diagram' + (plural ? 's' : '');
      case ProcedureType.STAR:
        return 'STAR' + (plural ? 's' : '');
      case ProcedureType.TakeoffMinimums:
        return 'Takeoff Minimums';
      case ProcedureType.IAP:
        return 'Approach Procedure' + (plural ? 's' : '');
      case ProcedureType.LAHSO:
        return 'LAHSO';
      case ProcedureType.DP:
        return 'Departure Procedure' + (plural ? 's' : '');
      case ProcedureType.AAUP:
        return '"Attention All Users" Page';
      case ProcedureType.ODP:
        return 'Obstacle Departure Procedure' + (plural ? 's' : '');
      case ProcedureType.CHS:
        return 'Chart Supplement' + (plural ? 's' : '');
      default:
        return null;
    }
  }

  /**
   * Parses change type
   * @param value {any} Change type as returned by API
   * @return {ChangeType} Parsed change type
   */
  static parseChangeType(value: any): ChangeType {
    switch (value) {
      case 'A':
        return ChangeType.Added;
      case 'C':
        return ChangeType.Changed;
      case 'D':
        return ChangeType.Deleted;
      default:
        return null;
    }
  }

  /**
   * Formats procedure change type for display
   * @param value {ChangeType} Procedure change type
   * @return {string} Formatted procedure change type
   */
  static formatChangeType(value: ChangeType): string {
    switch (value) {
      case ChangeType.Added:
        return 'New';
      case ChangeType.Changed:
        return 'Changed';
      case ChangeType.Deleted:
        return 'Removed';
      default:
        return null;
    }
  }
}


/**
 * Model for a set of terminal procedures that are all effective during the same data cycle.
 */
export class TerminalProcedureSet {
  /**
   * Effective from date of the set
   */
  effDateFrom: Date;

  /**
   * Effective to date of the set
   */
  effDateTo: Date;

  /**
   * Terminal procedures that are part of the set.
   */
  procedures: Array<TerminalProcedure>;

  /**
   * Parses terminal procedure set
   * @param data {any} Terminal procedure data as returned by API
   * @return {TerminalProcedure} Parsed terminal procedure set
   */
  static parse(data: any): TerminalProcedureSet {
    const procedures = (data || []).map(d => TerminalProcedure.parse(d));
    return Object.assign(new TerminalProcedureSet(), {
      effDateFrom: this.getEffDateFrom(procedures),
      effDateTo: this.getEffDateTo(procedures),
      procedures
    });
  }

  /**
   * Returns the "effective from" date of the terminal procedure set. This is based on the effective date
   * of the first procedure (other than the chart supplement, which is based on a different data cycle schedule).
   * @param {Array<TerminalProcedure>} procedures
   * @returns {Date}
   */
  static getEffDateFrom(procedures: Array<TerminalProcedure>): Date {
    const proc = procedures.filter(p => p.type !== ProcedureType.CHS)[0];
    return proc ? proc.effDateFrom : null;
  }

  /**
   * Returns the "effective to" date of the terminal procedure set. This is based on the effective date
   * of the first procedure (other than the chart supplement, which is based on a different data cycle schedule).
   * @param {Array<TerminalProcedure>} procedures
   * @returns {Date}
   */
  static getEffDateTo(procedures: Array<TerminalProcedure>): Date {
    const proc = procedures.filter(p => p.type !== ProcedureType.CHS)[0];
    return proc ? proc.effDateTo : null;
  }
}


/**
 * Model for a set of terminal procedures that are broken out by a "current" set (effective now),
 * and a "next" set (effective during the next data cycle).
 */
export class TerminalProcedurePack {
  /**
   * Terminal procedures that are effective now
   */
  current: TerminalProcedureSet;

  /**
   * Terminal procedures that are effective in the next data cycle (if available).
   */
  next: TerminalProcedureSet;

  /**
   * Parses terminal procedure pack
   * @param current {any} Current terminal procedure data as returned by API
   * @param next {any} Upcoming terminal procedure data as returned by API
   * @return {TerminalProcedure} Parsed terminal procedure pack
   */
  static parse(current: any, next: any): TerminalProcedurePack {
    const result = Object.assign(new TerminalProcedurePack(), {
      current: TerminalProcedureSet.parse(current),
      next: next ? TerminalProcedureSet.parse(next) : null,
    });

    // Chart supplements are published on a 56-day cycle, whereas the rest of the terminal procedures are published
    // on a 28-day cycle. This can result in a discrepancy where the chart supplements are only included in "current",
    // but not in "next" - even though they are still valid in the "next" 28-day data cycle. Here we detect this
    // scenario, and copy the chart supplements from current into next if they are still valid.
    if (result.next && result.next.procedures.length >= 0 && result.next.effDateFrom) {
      const currentChartSupplements = result.current.procedures.filter(p => p.type === ProcedureType.CHS);
      for (const proc of currentChartSupplements) {
        // Ensure the chart supplement is still valid in the next 28-day data cycle.
        if (proc.effDateTo.getTime() >= result.next.effDateFrom.getTime()) {
          // Ensure the chart supplement is not already included in the next 28-day cycle.
          if (result.next.procedures.findIndex(p => p.type === ProcedureType.CHS && p.airportId === proc.airportId) === -1) {
            // Manually add the chart supplement to the next data cycle
            result.next.procedures.push(proc);
          }
        }
      }
    }

    return result;
  }
}
